import {
  __async,
  __name
} from "./chunk-CRQHCYH4.js";

// src/tiptap/youtube.tsx
import { useRef, useState } from "react";
import cn from "classnames";
import { Node, mergeAttributes, nodePasteRule } from "@tiptap/core";
import { NodeViewWrapper, ReactNodeViewRenderer } from "@tiptap/react";
import { PlayCircleIcon } from "@heroicons/react/24/solid";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
var YOUTUBE_REGEX = /^(https?:\/\/)?(www\.|music\.)?(youtube\.com|youtu\.be)(?!.*\/channel\/)(?!\/@)(\/.+)$/;
var YOUTUBE_REGEX_GLOBAL = /^(https?:\/\/)?(www\.|music\.)?(youtube\.com|youtu\.be)(?!.*\/channel\/)(?!\/@)(\/.+)$/g;
var isValidYoutubeUrl = /* @__PURE__ */ __name((url) => {
  return !!url.match(YOUTUBE_REGEX);
}, "isValidYoutubeUrl");
var parseYoutubeVideoId = /* @__PURE__ */ __name((url) => {
  if (!url) {
    return null;
  }
  if (url.includes("/embed/")) {
    return url.split("/embed/").pop();
  }
  if (url.includes("youtu.be")) {
    return url.split("/").pop();
  }
  const videoIdRegex = /v=([-\w]+)/gm;
  const matches = videoIdRegex.exec(url);
  if (!matches || !matches[1] || matches[1].length !== 11) {
    return null;
  }
  return matches[1];
}, "parseYoutubeVideoId");
var _a;
var needsYTApiForAutoplay = typeof navigator !== "undefined" && (((_a = navigator.vendor) == null ? void 0 : _a.includes("Apple")) || navigator.userAgent.includes("Mobi"));
var ytPromise;
var fetchYTPlayerApi = /* @__PURE__ */ __name(() => {
  if (window.YT || window.YT && window.YT.Player)
    return;
  if (ytPromise)
    return ytPromise;
  ytPromise = new Promise((res, rej) => {
    window.onYouTubeIframeAPIReady = res;
    const el = document.createElement("script");
    el.src = "https://www.youtube.com/iframe_api";
    el.async = true;
    el.onerror = rej;
    document.body.append(el);
  });
  return ytPromise;
}, "fetchYTPlayerApi");
var YoutubeFacade = /* @__PURE__ */ __name(({ editor, node }) => {
  const src = node.attrs.src;
  const videoId = parseYoutubeVideoId(src);
  const thumbnail = `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
  const [preconnect, setPreconnect] = useState(false);
  const [iframeLoad, setIframeLoad] = useState(false);
  const apiPlaceholder = useRef(null);
  const loadVideo = /* @__PURE__ */ __name(() => __async(void 0, null, function* () {
    setIframeLoad(true);
    if (!needsYTApiForAutoplay)
      return;
    yield fetchYTPlayerApi();
    if (apiPlaceholder.current && videoId) {
      new YT.Player(apiPlaceholder.current, {
        videoId,
        width: "100%",
        playerVars: {
          autoplay: 1,
          playsinline: 1
        },
        events: {
          onReady: (event) => {
            console.log("4");
            event.target.mute();
            event.target.playVideo();
          },
          onError: (event) => console.log(event),
          onStateChange: (event) => console.log(event)
        }
      });
    }
  }), "loadVideo");
  return /* @__PURE__ */ jsxs(NodeViewWrapper, { children: [
    preconnect ? /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx("link", { rel: "preconnect", href: "https://www.youtube-nocookie.com" }),
      /* @__PURE__ */ jsx("link", { rel: "preconnect", href: "https://www.google.com" })
    ] }) : null,
    /* @__PURE__ */ jsxs(
      "article",
      {
        onPointerOver: () => setPreconnect(true),
        style: { backgroundImage: `url(${thumbnail})` },
        className: "cursor-pointer bg-cover bg-black bg-center relative block aspect-video rounded overflow-hidden mb-4 [&_iframe]:absolute [&_iframe]:inset-0 [&_iframe]:w-full [&_iframe]:h-full",
        "data-drag-handle": true,
        children: [
          !iframeLoad ? /* @__PURE__ */ jsx(
            "div",
            {
              className: cn(
                "absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center text-gray-200 hover:text-white transition-colors",
                { "pointer-events-none": editor.isEditable }
              ),
              onClick: loadVideo,
              children: /* @__PURE__ */ jsx(PlayCircleIcon, { className: "w-20 h-20" })
            }
          ) : null,
          /* @__PURE__ */ jsx("div", { ref: apiPlaceholder }),
          iframeLoad && !needsYTApiForAutoplay ? /* @__PURE__ */ jsx(
            "iframe",
            {
              width: "560",
              height: "315",
              allow: "accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture",
              allowFullScreen: true,
              src: `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&state=1`
            }
          ) : null
        ]
      }
    )
  ] });
}, "YoutubeFacade");
var YoutubeVideo = Node.create({
  name: "youtubeVideo",
  priority: 1e3,
  group: "block",
  draggable: true,
  addAttributes() {
    return {
      src: {
        default: null,
        parseHTML: (element) => {
          if (element.tagName.toLowerCase() === "ytvideo") {
            return element.getAttribute("src");
          }
          if (element.tagName.toLowerCase() === "iframe") {
            return element.getAttribute("src");
          }
          return null;
        }
      }
    };
  },
  addCommands() {
    return {
      setYoutubeVideo: (attributes) => ({ commands }) => {
        return commands.insertContent({
          type: this.name,
          attrs: attributes
        });
      }
    };
  },
  addPasteRules() {
    return [
      nodePasteRule({
        find: YOUTUBE_REGEX_GLOBAL,
        type: this.type,
        getAttributes: (match) => {
          console.log(match);
          return { src: match.input };
        }
      })
    ];
  },
  parseHTML() {
    return [
      {
        tag: "ytvideo"
      },
      {
        tag: "iframe",
        getAttrs: (node) => {
          var _a2;
          if ((_a2 = node.getAttribute("src")) == null ? void 0 : _a2.includes("youtube")) {
            return null;
          }
          return false;
        }
      }
    ];
  },
  renderHTML({ HTMLAttributes }) {
    const src = HTMLAttributes.src;
    const videoId = parseYoutubeVideoId(src);
    const thumbnail = `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
    const url = `https://www.youtube.com/watch?v=${videoId}`;
    return [
      "ytvideo",
      mergeAttributes(HTMLAttributes),
      [
        "a",
        {
          href: url,
          style: `background-image: url(${thumbnail})`,
          class: "bg-cover bg-black bg-center relative block aspect-video rounded overflow-hidden mb-4"
        },
        [
          "div",
          {
            class: "absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center text-gray-200 hover:text-white transition-colors"
          }
        ]
      ]
    ];
  },
  addNodeView() {
    return ReactNodeViewRenderer(YoutubeFacade);
  }
});
var youtube_default = YoutubeVideo;

export {
  isValidYoutubeUrl,
  parseYoutubeVideoId,
  youtube_default
};
//# sourceMappingURL=chunk-GALEMFKX.js.map